import { useQuery } from '@tanstack/react-query'
import {
  PiArchive,
  PiArrowCounterClockwise,
  PiCalendar,
  PiCopy,
  PiEye,
  PiListChecks,
  PiPaperPlaneTilt,
  PiPencilSimple,
  PiPlus,
  PiPushPin,
  PiPushPinSlash,
  PiRocket,
  PiUploadSimple,
  PiUser,
  PiUserPlus,
} from 'react-icons/pi'
import { TargetArrowIcon } from 'components/Layout/LeftNav/nav-icons'
import { HelpTooltip } from 'components/Tooltip/HelpTooltip'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { integerFormatterOrFallback } from 'utils/formatting'
import { useMemo, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { usePinTarget } from 'components/QuickAccess/usePinTarget'
import { useRecordVisit } from 'components/QuickAccess/useRecordVisit'
import { useInvalidateCampaignContactRequests } from 'services/contactRequest'
import FbButton from 'components/FbUI/FbButton'
import { Ds2026ButtonProvider } from 'components/FbUI/Ds2026ButtonContext'
import { NoticeModal } from 'components/FbUI/NoticeModal'
import {
  ActionsDropdown,
  type ActionItem,
} from 'components/Buttons/ActionsDropdown'
import { ds2026DropdownTriggerButtonClass } from 'components/FbUI/ds2026DropdownMenu'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from 'components/UI/Modal/ModalPrimitive'
import { FBPage } from 'components/FbUI/Page/FBPage'
import {
  FBPageHeader,
  FBPageInnerTabsContainer,
  FBPageTableContainer,
  FBPageTitle,
  FBPageTitleRow,
} from 'components/FbUI/Page/styles'
import { UnderlineTabs } from 'components/FbUI/UnderlineTabs'
import { ContactRequestJobFromCampaignModal } from 'components/Modals/ContactRequestJobFromCampaignModal'
import { ContactRequestsTable } from 'components/Tables/ContactRequestsTable/ContactRequestsTable'
import { cn } from 'components/UI/cn'
import {
  CONTACT_REQUEST_JOB_TABLE_KEY,
  getCampaignTableKey,
} from 'constants/tableQueryKeys'
import { useEntityBreadcrumbs } from 'context/layout'
import { ArchiveCampaignModal } from 'features/campaigns/ArchiveCampaignModal'
import { useGetCampaignSettings } from 'services/campaigns'
import { CampaignDetailContactsTab } from 'features/campaigns/CampaignDetailContactsTab'
import { CampaignDetailOverviewTab } from 'features/campaigns/CampaignDetailOverviewTab'
import { CampaignSourceFilterBadge } from 'features/campaigns/CampaignSourceFilterBadge'
import { CreateSequenceFromExistingModal } from 'features/campaigns/CreateSequenceFromExistingModal'
import { CampaignDetailTargetsTab } from 'features/campaigns/CampaignDetailTargetsTab'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { ToolbarDensityProvider } from 'components/Tables/CommonTableCells/ToolbarDensityContext'
import {
  useArchiveCampaign,
  useGetCampaignById,
  useGetCampaignCompanies,
  useUnarchiveCampaign,
} from 'features/campaigns/campaigns_api'
import { EditCampaignModalV2 } from 'features/campaigns/EditCampaignModalV2'
import { useGetCampaignSequence } from 'services/sequences'
import { WORKFLOW_STATUS } from 'models/campaign'
import { ALLOWED_CONTACT_REQUEST_TYPES } from 'models/companies'
import apiService from 'services/api'
import { toast } from 'react-toastify'
import { NotFoundContent } from '../not-found-content'

type DetailTab = 'overview' | 'targets' | 'contacts' | 'contact_requests'

export function CampaignDetailPageV2() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const featureFlags = useFeatureFlagService()
  const [activeTab, setActiveTab] = useState<DetailTab>(() => {
    const t = searchParams.get('tab')
    const allowed: DetailTab[] = [
      'overview',
      'targets',
      'contacts',
      'contact_requests',
    ]
    return (allowed as string[]).includes(t ?? '')
      ? (t as DetailTab)
      : 'overview'
  })
  const [showEditModal, setShowEditModal] = useState(false)
  const [showCreateFromExisting, setShowCreateFromExisting] = useState(false)
  const [showContactRequestModal, setShowContactRequestModal] = useState(false)
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false)
  const [showNoTargetsNotice, setShowNoTargetsNotice] = useState(false)
  const archiveMutation = useArchiveCampaign()
  const unarchiveMutation = useUnarchiveCampaign()
  const api = apiService()
  const campaignId = parseInt(id ?? '-1')
  const { enableQuickAccess } = useFeatureFlagService()
  const {
    isPinned: pinnedToHome,
    toggle: togglePin,
    isPending: pinPending,
  } = usePinTarget({ targetType: 'campaign', targetId: campaignId })
  useRecordVisit(
    campaignId > 0 ? { targetType: 'campaign', targetId: campaignId } : null
  )
  const { data: campaign, isLoading } = useGetCampaignById(campaignId)
  const { data: sequence } = useGetCampaignSequence(campaignId)
  const { data: settings } = useGetCampaignSettings(campaignId)
  const { preferences } = usePreferences()
  const sendCapTooltip = preferences?.tooltips?.campaigns_daily_send_cap
  const sharingCount = settings?.active_campaigns_sharing_count ?? 0
  const { data: campaignCompanies } = useGetCampaignCompanies(campaignId)
  const { data: contactsData } = useQuery({
    queryKey: ['campaign', campaignId, 'contacts-count'],
    queryFn: () =>
      api.getContactsList({
        campaign: campaignId,
        campaign_page: true,
        type: 'all',
        limit: 1,
        page: 1,
      }),
    enabled: !!campaign,
    staleTime: 1000 * 60 * 5,
  })
  const breadcrumbs = useMemo(
    () => (campaign ? [{ label: campaign.name }] : []),
    [campaign]
  )
  useEntityBreadcrumbs(breadcrumbs)

  const hasContactRequests = useMemo(() => {
    if (!campaign) return false
    // Multi-type campaigns (type is null) always show contact requests — the
    // subcategory picker inside the wizard handles per-type eligibility.
    if (!campaign.type) return true
    return ALLOWED_CONTACT_REQUEST_TYPES.includes(campaign.type.slug as any)
  }, [campaign])
  const { data: contactRequestsData } = useQuery({
    queryKey: ['campaign', campaignId, 'contact-requests-count'],
    queryFn: async () =>
      api.fetchContactRequestJobs({ campaign: campaignId, limit: 1, page: 1 }),
    enabled: !!campaign && hasContactRequests,
    staleTime: 1000 * 60 * 5,
  })

  const targetCount = campaignCompanies?.total_count ?? 0
  const contactCount = contactsData?.count ?? 0
  const totalContactRequestsCount = contactRequestsData?.count ?? 0

  const handleContactRequested =
    useInvalidateCampaignContactRequests(campaignId)

  const tabsWithCounts: {
    key: DetailTab
    title: string
    count?: number | null
  }[] = useMemo(() => {
    const tabs: {
      key: DetailTab
      title: string
      count?: number | null
    }[] = [
      { key: 'overview', title: 'Overview' },
      { key: 'targets', title: 'Targets', count: targetCount },
      { key: 'contacts', title: 'Contacts', count: contactCount },
    ]
    if (hasContactRequests) {
      tabs.push({
        key: 'contact_requests',
        title: 'Contact Requests',
        count: totalContactRequestsCount,
      })
    }
    return tabs
  }, [targetCount, contactCount, hasContactRequests, totalContactRequestsCount])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spinner animation="border" />
      </div>
    )
  }
  if (!campaign) {
    return <NotFoundContent />
  }

  const formattedLaunchDate = campaign.launch_date
    ? new Date(campaign.launch_date).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : 'Not set'
  const hasTargets = targetCount > 0
  const hasContacts = contactCount > 0
  const hasSequence = !!sequence
  const hasLaunchDate = !!campaign.launch_date
  const campaignStatus = settings?.status ?? 'draft'
  const isActiveCampaign =
    campaignStatus === 'active' ||
    campaignStatus === 'queued' ||
    campaignStatus === 'launching'
  const isPausedCampaign = campaignStatus === 'paused'
  // Request Contacts surfaces as a tertiary header link only while drafting with
  // targets added; in every other state it lives in the Actions menu.
  const showRequestContactsLink =
    campaignStatus === 'draft' && hasTargets && hasContactRequests
  const sequenceButtonLabel = isActiveCampaign
    ? 'View Sequence'
    : isPausedCampaign
      ? 'Edit Sequence'
      : !hasSequence
        ? 'Create Sequence'
        : hasContacts && hasLaunchDate
          ? 'Preview & Launch'
          : 'Continue Setup'
  const SequenceButtonIcon = isActiveCampaign
    ? PiEye
    : isPausedCampaign
      ? PiPencilSimple
      : !hasSequence
        ? PiPlus
        : hasContacts && hasLaunchDate
          ? PiRocket
          : PiListChecks
  const statusConfig: Record<string, { label: string; className: string }> = {
    draft: {
      label: 'Draft',
      className: 'bg-gray-100 text-gray-700',
    },
    active: {
      label: 'Active',
      className: 'bg-green-50 text-green-700',
    },
    launching: {
      label: 'Launching',
      className: 'bg-green-50 text-green-700',
    },
    queued: {
      label: 'Queued',
      className: 'bg-green-50 text-green-700',
    },
    paused: {
      label: 'Paused',
      className: 'bg-amber-50 text-amber-700',
    },
    completed: {
      label: 'Completed',
      className: 'bg-green-50 text-green-700',
    },
    stopped: {
      label: 'Stopped',
      className: 'bg-red-50 text-red-700',
    },
    failed: {
      label: 'Failed',
      className: 'bg-red-50 text-red-700',
    },
    archived: {
      label: 'Archived',
      className: 'bg-gray-100 text-gray-700',
    },
    'pending deletion': {
      label: 'Pending Deletion',
      className: 'bg-red-50 text-red-700',
    },
    deleted: {
      label: 'Deleted',
      className: 'bg-gray-100 text-gray-700',
    },
  }
  const badge = statusConfig[campaignStatus] ?? statusConfig.draft

  const actions: ActionItem[] = [
    {
      label: 'Edit Campaign',
      icon: PiPencilSimple,
      onClick: () => setShowEditModal(true),
    },
    // "Create from existing" is only meaningful for an editable campaign that
    // has no sequence yet — mirrors the empty-state card's visibility (FIR-7659).
    ...(!hasSequence && !campaign.is_archived
      ? [
          {
            label: 'Create from existing',
            icon: PiCopy,
            onClick: () => setShowCreateFromExisting(true),
          },
        ]
      : []),
    ...(featureFlags.enableContactImportWizard
      ? [
          {
            label: 'Import contacts',
            icon: PiUploadSimple,
            onClick: () => navigate(`/contacts/import?campaign=${campaignId}`),
          },
        ]
      : []),
    ...(enableQuickAccess
      ? [
          {
            label: pinnedToHome ? 'Unpin from homepage' : 'Pin to homepage',
            icon: pinnedToHome ? PiPushPinSlash : PiPushPin,
            onClick: () => {
              togglePin()
              toast.success(
                pinnedToHome ? 'Unpinned from homepage' : 'Pinned to homepage'
              )
            },
            disabled: pinPending,
          },
        ]
      : []),
    ...(hasContactRequests && !showRequestContactsLink
      ? [
          {
            label: 'Request Contacts',
            icon: PiUserPlus,
            onClick: () => {
              if (targetCount === 0) {
                setShowNoTargetsNotice(true)
              } else {
                setShowContactRequestModal(true)
              }
            },
          },
        ]
      : []),
    campaign.is_archived
      ? {
          label: 'Unarchive Campaign',
          icon: PiArrowCounterClockwise,
          onClick: () => unarchiveMutation.mutate(campaign.id),
          disabled: unarchiveMutation.isPending,
        }
      : {
          label: 'Archive Campaign',
          icon: PiArchive,
          onClick: () => setShowArchiveConfirm(true),
        },
  ]

  return (
    <>
      <FBPage
        // Tighter outer padding matching the other DS2026 table pages so the
        // content gets more of the viewport; global --page-*-padding untouched.
        innerPageClassName="!bg-surface-page !p-[8px]"
        containerClassName="!bg-surface-page !shadow-none !px-[16px] !py-[12px]"
      >
        <FBPageHeader>
          <FBPageTitleRow className="w-auto flex-col !items-start gap-1">
            <span
              className={cn(
                'inline-flex items-center rounded-2xl border-0 px-2.5 py-0.5 text-sm font-medium leading-5 tracking-[-0.2px]',
                badge.className
              )}
              style={
                campaignStatus === 'draft'
                  ? { backgroundColor: '#f3f4f6', color: '#374151' }
                  : undefined
              }
            >
              {badge.label}
            </span>
            <FBPageTitle>
              {campaign.name}
              {campaign.type && (
                <span className="ml-2 align-middle text-base font-normal text-gray-500">
                  · {campaign.type.name}
                </span>
              )}
            </FBPageTitle>
          </FBPageTitleRow>
          <Ds2026ButtonProvider>
            <div className="flex items-center gap-3">
              {showRequestContactsLink && (
                <FbButton
                  variant="secondary"
                  onClick={() => setShowContactRequestModal(true)}
                  className="whitespace-nowrap"
                >
                  <PiUserPlus className="size-4" />
                  <span>Request Contacts</span>
                </FbButton>
              )}
              <FbButton
                onClick={() => navigate(`/campaigns/${id}/sequence`)}
                variant="primary"
                className="whitespace-nowrap"
              >
                <SequenceButtonIcon className="size-4" />
                <span>{sequenceButtonLabel}</span>
              </FbButton>
              <ActionsDropdown
                actions={actions}
                orientation="horizontal"
                triggerClassName={ds2026DropdownTriggerButtonClass}
              />
            </div>
          </Ds2026ButtonProvider>
        </FBPageHeader>
        <div className="flex items-center gap-6 mb-4">
          <span className="flex items-center gap-1.5 text-sm text-gray-500 tracking-[-0.2px]">
            <PiCalendar className="size-4 text-brand-active" />
            Projected:{' '}
            <span className="font-medium text-gray-900">
              {formattedLaunchDate}
            </span>
          </span>
          {hasTargets && (
            <span className="flex items-center gap-1.5 text-sm text-gray-500 tracking-[-0.2px]">
              <TargetArrowIcon size={16} className="text-brand-active" />
              Targets:{' '}
              <span className="font-medium text-gray-900">{targetCount}</span>
            </span>
          )}
          {campaign.owner_name && (
            <span className="flex items-center gap-1.5 text-sm text-gray-500 tracking-[-0.2px]">
              <PiUser className="size-4 text-brand-active" />
              Owner:{' '}
              <span className="font-medium text-gray-900">
                {campaign.owner_name}
              </span>
            </span>
          )}
          <CampaignSourceFilterBadge campaign={campaign} />
          {settings && sharingCount > 1 && (
            <span className="flex items-center gap-1.5 text-sm text-gray-500 tracking-[-0.2px]">
              <PiPaperPlaneTilt className="size-4 text-brand-active" />
              Sending:{' '}
              <span className="font-medium text-gray-900">
                {integerFormatterOrFallback(settings.max_emails_per_day)}/day
              </span>
              <span className="text-fg-tertiary">
                · shared by {sharingCount}
              </span>
              {sendCapTooltip && (
                <HelpTooltip
                  title={sendCapTooltip.title}
                  content={sendCapTooltip.content}
                />
              )}
            </span>
          )}
        </div>
        <FBPageInnerTabsContainer>
          <UnderlineTabs<DetailTab>
            tabs={tabsWithCounts}
            activeKey={activeTab}
            onChange={setActiveTab}
            idPrefix="campaign-detail"
            aria-label="Campaign sections"
          />
        </FBPageInnerTabsContainer>
        <div className="flex flex-col flex-1 min-h-0">
          {activeTab === 'overview' && (
            <CampaignDetailOverviewTab
              campaign={campaign}
              campaignId={id!}
              hasSequence={hasSequence}
              onCreateFromExisting={
                campaign.is_archived
                  ? undefined
                  : () => setShowCreateFromExisting(true)
              }
            />
          )}
          {activeTab === 'targets' && (
            <CampaignDetailTargetsTab campaign={campaign} />
          )}
          {activeTab === 'contacts' && (
            <CampaignDetailContactsTab
              campaign={campaign}
              onSwitchToTargets={() => setActiveTab('targets')}
              onViewContactRequests={() => setActiveTab('contact_requests')}
              onContactRequested={handleContactRequested}
            />
          )}
          {activeTab === 'contact_requests' && (
            <TableLinkStyleProvider variant="plain">
              {/* sm toolbar density (32px search + Filter/Columns), matching
                  the standalone table pages. */}
              <ToolbarDensityProvider value="sm">
                <FBPageTableContainer
                  className="overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]"
                  style={{
                    height: 'calc(var(--base-page-table-height) - 102px)',
                  }}
                >
                  <ContactRequestsTable
                    tableKey={getCampaignTableKey(
                      CONTACT_REQUEST_JOB_TABLE_KEY,
                      campaign.id
                    )}
                    campaignId={campaign.id}
                  />
                </FBPageTableContainer>
              </ToolbarDensityProvider>
            </TableLinkStyleProvider>
          )}
        </div>
      </FBPage>
      <EditCampaignModalV2
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        campaign={campaign}
        campaignId={campaign.id}
      />
      <CreateSequenceFromExistingModal
        show={showCreateFromExisting}
        onHide={() => setShowCreateFromExisting(false)}
        currentCampaignId={campaignId}
      />
      <ContactRequestJobFromCampaignModal
        campaignId={campaign.id}
        isMultiType={!campaign.type}
        externalOpen={showContactRequestModal}
        onExternalClose={() => setShowContactRequestModal(false)}
        onContactRequested={handleContactRequested}
      />
      <ArchiveCampaignModal
        show={showArchiveConfirm}
        onHide={() => setShowArchiveConfirm(false)}
        campaignName={campaign.name}
        isActive={campaignStatus === WORKFLOW_STATUS.ACTIVE}
        onConfirm={() => {
          archiveMutation.mutate(campaign.id, {
            onSuccess: () => navigate('/campaigns'),
          })
        }}
        isLoading={archiveMutation.isPending}
      />
      <Dialog open={showNoTargetsNotice} onOpenChange={setShowNoTargetsNotice}>
        <DialogContent
          noPadding
          disableCloseIcon
          aria-describedby={undefined}
          className="!w-auto !max-w-none !overflow-visible !border-0 !bg-transparent !py-0 !shadow-none"
        >
          <DialogTitle className="sr-only">Add targets first</DialogTitle>
          <NoticeModal
            icon={TargetArrowIcon}
            title="Add targets first"
            description="Request Contacts pulls contacts for the companies in your campaign. Add a few targets, then come back to request."
            onClose={() => setShowNoTargetsNotice(false)}
            actions={[
              {
                label: 'Cancel',
                variant: 'secondary',
                onClick: () => setShowNoTargetsNotice(false),
              },
              {
                label: 'Add Targets',
                variant: 'primary',
                icon: TargetArrowIcon,
                onClick: () => {
                  setShowNoTargetsNotice(false)
                  setActiveTab('targets')
                },
              },
            ]}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
